/* eslint-disable prefer-const */
import { Component, Input, forwardRef, Output, ViewChild, ElementRef } from '@angular/core';
import { MessageService } from 'primeng/api';

import { AbstractFormFieldComponent } from '../../FormField/abstract-form-field.component';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { ImageUploadFieldModel } from '../../../models/image-upload-field.model';
import { ConfirmDialogModel } from '../../../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { getReadableFileSize } from '../../../../../Core/utils';
import { ConfirmDialogComponent } from '../../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaResultModel, MediaServiceErrorCodesEnum } from '../../../../../api/mediaService/mediaResults.api.model';


@Component({
    selector: 'agpr-image-upload-field',
    templateUrl: './image-upload-field.component.html',
    styleUrls: ['./image-upload-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => ImageUploadFieldComponent)
        },
    ],
    standalone: false
})
export class ImageUploadFieldComponent extends AbstractFormFieldComponent {
    @Input() declare field: ImageUploadFieldModel;
    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public mediaServiceApi: MediaServiceApi
    ) {
        super();
    }

    public resetInputValue(input: any): void {
        input.value = '';
    }

    public handleFileInput(file: File, input: any): void {
        if (!file) {
            this.resetInputValue(input);
            return;
        }
        const supportedFileTypes = this.field.accepts.replace(/, /g, '|');
        if (supportedFileTypes && !file.type.match(supportedFileTypes)) {
            // error
            this.resetInputValue(input);
            // @TODO: This error message needs re-worked, the list that needs to be passed in via supportedFileTypes is not human readable
            //  and shouldn't be part of the error message
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                detail: this.localizationService.transform(
                    'PageBuilder.error.fileTypeMessage',
                    {
                        types: this.field.accepts.replace(/image\//g, '')
                    }
                )
            });
            return;
        }
        if (this.field.maxSizeBytes && this.field.maxSizeBytes < file.size) {
            // error
            this.resetInputValue(input);
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage.specific', {
                    size: getReadableFileSize(this.field.maxSizeBytes)
                })
            });
            return;
        }

        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).subscribe({
            next: result => {
                const imageMediaResult: MediaResultModel = result?.mediaResults?.find(mediaResult => mediaResult?.media?.name === file.name);

                if (imageMediaResult) {
                    const url = imageMediaResult?.media?.url;
                    const errorCode = imageMediaResult?.codedError?.code;

                    if (errorCode) {
                        this.handleErrorCode(errorCode, input);

                        return;
                    }

                    this.changes.emit(url);
                    this.resetInputValue(input);
                } else {
                    const error = result.mediaResults.find(mediaResult => !!mediaResult.codedError);

                    this.handleErrorCode(error?.codedError?.code, input);
                }
            },
            error: (error) => {
                if (error.status === 429) {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                    });
                }
            }
        });
    }

    handleErrorCode(errorCode: MediaServiceErrorCodesEnum, input) {
        if (errorCode === MediaServiceErrorCodesEnum.insufficientSpace) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'PageBuilder.error.imageLimitHeader',
                    messageHtml: 'PageBuilder.error.imageLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'PageBuilder.error.imageLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.resetInputValue(input);
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.fileMaxSize) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage')
            });

            return;
        }

        if (errorCode !== null) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.imageUploadFailed'),
                detail: this.localizationService.transform('PageBuilder.error.imageUploadFailedMessage')
            });
        }
    }
}
